import { create } from 'zustand';
import { persist } from 'zustand/middleware';
import { RenderSettings, defaultRenderSettings, QualityPresets } from '../rendering/RenderingConstants';
import { settingsStorage } from '../utils/settingsPersistence';

export type QualityPresetName = keyof typeof QualityPresets;

/**
 * Render Settings State
 *
 * Single source of truth for viewport rendering configuration.
 * Wired: RenderSettingsPanel -> this store -> Canvas dpr / PostProcessing / AdvancedLighting
 */
interface RenderSettingsState {
    settings: RenderSettings;
    activePreset: QualityPresetName | 'custom';

    setSetting: <K extends keyof RenderSettings>(key: K, value: RenderSettings[K]) => void;
    updateSettings: (partial: Partial<RenderSettings>) => void;
    applyPreset: (name: QualityPresetName) => void;
}

export const useRenderSettingsStore = create<RenderSettingsState>()(
    persist(
        (set) => ({
            settings: { ...defaultRenderSettings },
            activePreset: 'custom',

            setSetting: (key, value) =>
                set((state) => ({
                    settings: { ...state.settings, [key]: value },
                    activePreset: 'custom',
                })),

            updateSettings: (partial) =>
                set((state) => ({
                    settings: { ...state.settings, ...partial },
                    activePreset: 'custom',
                })),

            applyPreset: (name) =>
                set(() => ({
                    settings: { ...defaultRenderSettings, ...QualityPresets[name] },
                    activePreset: name,
                })),
        }),
        {
            name: 'yap-render-settings',
            storage: settingsStorage,
            partialize: (state) => ({
                settings: state.settings,
                activePreset: state.activePreset,
            }),
        }
    )
);
